import { motion } from "framer-motion"
import { createContext, ReactNode, useContext, useEffect } from "react"
import { useFormContext } from "react-hook-form"

interface ModalContext {
  isOpen: boolean
  onClose: any
}

interface Modal extends ModalContext {
  onSave: () => {}
  saveTitleButton: string
  children: ReactNode
}

export const ModalContex = createContext<ModalContext>({} as ModalContext)

export const UseModalContext = () => {
  const context = useContext(ModalContex)
  return context
}

const variants = {
  open: { opacity: 1, y: 0 },
  closed: { opacity: 0, y: 100 },
}

export default function Modal(props: {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
}) {
  const handleEscape = (e: any) => {
    if (e.keyCode === 27) {
      props.onClose()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [])

  return (
    props.isOpen && (
      <ModalContex.Provider
        value={{ isOpen: props.isOpen, onClose: props.onClose }}
      >
        <div
          className="relative z-50"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="fixed inset-0 bg-gray-500/75 transition-opacity"
            aria-hidden="true"
            onClick={(e) => e.stopPropagation()}
          ></div>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <motion.div
                animate={props.isOpen ? "open" : "closed"}
                variants={variants}
                initial="closed"
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative transform overflow-hidden rounded-lg bg-gray-50 text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-2xl p-10 z-50"
              >
                {props.children}
              </motion.div>
            </div>
          </div>
        </div>
      </ModalContex.Provider>
    )
  )
}

const ModalHeader = (props: { children: ReactNode }) => {
  return (
    <div className="container flex justify-center">
      <h1 className="text-4xl md:text-6xl font-bold text-[#001a50] mb-6 leading-tight">
        {props.children}
      </h1>
      <ModalDismissButton styles="absolute top-2 right-5 text-gray-700 hover:text-gray-900 text-3xl">
        &times;
      </ModalDismissButton>
    </div>
  )
}

const ModalBody = (props: { children: ReactNode }) => {
  return <div className="flex flex-col">{props.children}</div>
}
const ModalFooter = (props: { children: ReactNode }) => {
  return (
    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
      {props.children}
    </div>
  )
}

const ModalDismissButton = (props: { children: ReactNode; styles: string }) => {
  const { onClose } = useContext(ModalContex)
  return (
    <button
      onClick={onClose}
      type="button"
      className={props.styles}
      aria-label="Close Modal"
    >
      {props.children}
    </button>
  )
}
const ModalActionButton = (props: {
  children: ReactNode
  styles: string
  type: "reset" | "submit" | "button" | undefined
  onClick?: () => void
}) => {
  const formContext = useFormContext()
  const { onClose } = useContext(ModalContex)

  const handleClick = async () => {
    if (props.onClick) {
      // If custom onClick logic is passed, run it
      props.onClick()
      onClose()
    }
    // Trigger form validation before checking errors
    const isValid = await formContext.trigger() // Trigger validation

    if (props.type === "submit" && !isValid) {
      console.log("Form has errors:", formContext.formState.errors) // Logs the errors now
      return // Stop here if there are errors, preventing the modal from closing.
    }

    if (props.type === "submit" && isValid) {
      setTimeout(() => {
        console.log("Form is valid. Closing modal.")
        onClose()
      }, 1000)
    }
  }

  return (
    <button
      onClick={handleClick}
      type={props.type}
      className={props.styles}
      aria-label="Action Modal"
    >
      {props.children}
    </button>
  )
}

Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter
Modal.Dismiss = ModalDismissButton
Modal.Action = ModalActionButton
